import { markRaw } from 'vue';
import { mapState } from 'vuex';
import { ContractChartGroup, ContractChartGroupStock } from '@/utils/chart-models';
import { applyChartTheme } from '@/utils/chart-theme';
import { LIVE_CHART_SOURCE_FACTORY } from '../live/live-chart-context';
import { attachLiveChartOverlay } from '../live/live-chart-overlay';
import { getEligibleContractIds, getLiveSeedPrices } from '../live/live-chart-policy';
import { createLiveChartSession } from '../live/live-chart-session';
import { createLivePriceFeed } from '../live/live-price-feed';

export default {
  inject: { liveSourceFactory: { from: LIVE_CHART_SOURCE_FACTORY, default: null } },
  props: {
    liveUpdatesEligible: { type: Boolean, default: false },
    liveHistoryReady: { type: Boolean, default: false },
    historyLoading: { type: Boolean, default: false },
    chartData: {
      type: Array,
      required: true,
      default: null,
    },
    chartDataLoaded: {
      type: Boolean,
      default: false,
    },
    chartOptions: {
      type: Object,
      default: null,
    },
    market: {
      type: Object,
      required: true,
    },
    timeline: {
      type: String,
      required: false,
    },
    chartType: {
      type: String,
      required: false,
    },
    chartSystemOptions: {
      type: Object,
      default: () => {},
    },
    showVolume: {
      type: Boolean,
      default: false,
    },
    showPriceAxisZoom: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      chart: null,
      liveSession: null,
      liveOverlay: null,
      renderTimer: null,
      disposed: false,
    };
  },
  computed: {
    liveContractIds() {
      return getEligibleContractIds({
        market: this.market,
        contractIds: this.chartData.map((row) => row.contractId),
      });
    },
    liveContext() {
      return JSON.stringify([
        this.liveUpdatesEligible,
        this.liveHistoryReady,
        this.historyLoading,
        this.timeline,
        this.market.marketId,
        this.liveContractIds,
      ]);
    },
    ...mapState('app', ['resolvedTheme', 'firebaseGeneration']),
    containerStyleModifiers() {
      let height = '390px';
      if (this.chartOptions && this.chartOptions.maxHeight) {
        height = this.chartOptions.maxHeight;
      }
      if (this.chartSystemOptions && this.chartSystemOptions.maxHeight) {
        height = this.chartSystemOptions.maxHeight;
      }
      return {
        height,
      };
    },
  },
  watch: {
    firebaseGeneration() {
      if (!this.liveSourceFactory && this.chart && !this.renderTimer && !this.disposed) {
        this.stopLive();
        this.startLive();
      }
    },
    showVolume: { handler: 'scheduleRender', flush: 'sync' },
    showPriceAxisZoom: { handler: 'scheduleRender', flush: 'sync' },
    chartType: { handler: 'scheduleRender', flush: 'sync' },
    chartData: { handler: 'scheduleRender', flush: 'sync' },
    resolvedTheme: { handler: 'scheduleRender', flush: 'sync' },
    liveContext: { handler: 'scheduleRender', flush: 'sync' },
  },
  methods: {
    stopLive() {
      this.liveSession?.stop();
      this.liveSession = null;
      this.liveOverlay?.dispose();
      this.liveOverlay = null;
    },
    destroyChart() {
      this.stopLive();
      this.chart?.destroy();
      this.chart = null;
    },
    scheduleRender() {
      clearTimeout(this.renderTimer);
      if (this.liveSession) this.liveSession.stop();
      this.liveSession = null;
      if (this.disposed) return;
      if (!this.chartData.length) {
        this.destroyChart();
        return;
      }
      if (this.liveUpdatesEligible && this.historyLoading) {
        this.stopLive();
        return;
      }
      this.renderTimer = setTimeout(() => {
        this.renderTimer = null;
        this.renderChart();
      }, ContractChartGroup.renderTimeout());
    },
    startLive() {
      if (!this.liveUpdatesEligible || !this.liveHistoryReady || !this.liveContractIds.length)
        return;
      this.liveOverlay = markRaw(attachLiveChartOverlay(this.chart));
      const createFeed =
        this.liveSourceFactory ||
        ((options) =>
          createLivePriceFeed({
            ...options,
            getDatabase: () =>
              this.$store.state.app.firebaseInitialized && window.firebase?.apps?.length
                ? window.firebase.database()
                : null,
          }));
      this.liveSession = markRaw(
        createLiveChartSession({
          contractIds: this.liveContractIds,
          createFeed,
          overlay: this.liveOverlay,
        }),
      );
    },
    renderChart() {
      if (this.disposed || !this.$refs.chart || !this.chartData.length) return;
      if (this.liveUpdatesEligible && this.historyLoading) return;
      this.destroyChart();
      const ClassName = this.chartType === 'candle' ? ContractChartGroupStock : ContractChartGroup;
      const options = { ...this.chartOptions };
      options.showVolume = this.showVolume;
      options.showPriceAxisZoom = this.showPriceAxisZoom;
      options.reserveLiveSlot =
        this.liveUpdatesEligible && this.liveHistoryReady && this.liveContractIds.length > 0;
      // Draw Now from the market's prices, so the price axis already fits it.
      options.liveSeedPrices = options.reserveLiveSlot
        ? getLiveSeedPrices({ market: this.market, contractIds: this.liveContractIds })
        : null;
      const group = new ClassName({
        market: this.market,
        timeline: this.timeline,
        data: this.chartData,
        chartOptions: options,
      });
      this.chart = markRaw(group.draw(this.$refs.chart));
      applyChartTheme(this.chart);
      this.startLive();
    },
  },
  mounted() {
    this.scheduleRender();
  },
  beforeUnmount() {
    this.disposed = true;
    clearTimeout(this.renderTimer);
    this.destroyChart();
  },
};
